import React, { useEffect, useRef, useState } from "react";
import checkIcon from "../../../assets/images/ic_completed_success.png";
import navigoServices from "../../../services/NavigoServices";
import { CircularProgress } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { logEvent } from "../../../app/eventSlice";
import { setStateFromPayload } from "../navigoSlice";
import {
  uploadNavigoDocuments,
  mapDocumentsToAppointment,
} from "../utilityFunctions";

function BookAfterPayment({ orderId, handleGoBack }) {
  const [status, setStatus] = useState(0);
  const totalTime = useRef(0);
  const intervalRef = useRef(null);
  // 1s poll that resolves the paid appointment's id (by paymentOrderId) so the
  // uploaded documents can be mapped to it.
  const filterIntervalRef = useRef(null);
  const filterAttemptsRef = useRef(0);
  const filterBusyRef = useRef(false);
  // Guards the finalize (upload + appointment lookup) so overlapping zpay poll
  // ticks can't start it twice.
  const isFinalizingRef = useRef(false);
  // Cache the uploaded documentIds so retries don't re-upload the same files.
  const uploadedDocIdsRef = useRef(null);
  const dispatch = useDispatch();
  const [isPB, setIsPB] = useState(false);

  // Files stashed at payment time (SMO / consultation with documents). Kept in
  // a ref so the interval closures always see the latest value.
  const pendingPaidDocs = useSelector(
    (store) => store.navigo.ui.pendingPaidDocs,
  );
  const pendingPaidDocsRef = useRef(pendingPaidDocs);
  useEffect(() => {
    pendingPaidDocsRef.current = pendingPaidDocs;
  }, [pendingPaidDocs]);

  useEffect(() => {
    const source = localStorage.getItem("app_source");
    setIsPB(source === "pb");
  }, []);
  useEffect(() => {
    dispatch(logEvent({ action: `payment complete` }));
    intervalRef.current = setInterval(callAndCheckStatus, 4000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (filterIntervalRef.current) clearInterval(filterIntervalRef.current);
    };
  }, []);
  const cancelIntervalAndSetStatus = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    setStatus(2);
  };

  // Once the transaction is successful: upload the documents, then poll the
  // (bare) appointments-by-filter API — sending only the ZO- orderId +
  // patientId — every 2s, up to 20 times, until the paid appointment exists,
  // then map each uploaded document to it.
  const finalizePaidDocs = async () => {
    const pending = pendingPaidDocsRef.current;
    try {
      if (uploadedDocIdsRef.current === null) {
        uploadedDocIdsRef.current = await uploadNavigoDocuments(
          pending.files,
          pending.patientId,
        );
      }
    } catch (e) {
      console.error("Paid doc upload failed", e);
    }

    filterAttemptsRef.current = 0;
    filterIntervalRef.current = setInterval(pollAppointmentAndMap, 2000);
  };

  const pollAppointmentAndMap = async () => {
    if (filterBusyRef.current) return; // don't overlap a slow request
    filterBusyRef.current = true;
    filterAttemptsRef.current += 1;

    // Safety cap: at most 20 attempts (~40s) so a never-created appointment
    // doesn't poll forever.
    if (filterAttemptsRef.current > 20) {
      if (filterIntervalRef.current) clearInterval(filterIntervalRef.current);
      console.error("Paid doc mapping: appointment never fetched", orderId);
      handleGoBack(2);
      return;
    }

    let appointmentId;
    try {
      const pending = pendingPaidDocsRef.current;
      const filterRes = await navigoServices.getAppointmentsByFilter({
        paymentOrderId: orderId,
        patientId: pending.patientId,
        pageSize: 10,
        pageNum: 1,
      });
      appointmentId = filterRes?.data?.appointments?.[0]?.id;
    } catch (e) {
      console.error("Paid doc mapping: appointment filter failed", e);
    }

    if (!appointmentId) {
      filterBusyRef.current = false; // keep polling every second
      return;
    }

    if (filterIntervalRef.current) clearInterval(filterIntervalRef.current);
    try {
      await mapDocumentsToAppointment(
        appointmentId,
        uploadedDocIdsRef.current || [],
      );
    } catch (e) {
      console.error("Post-payment document mapping failed", e);
    }
    dispatch(setStateFromPayload({ pendingPaidDocs: null }));
    handleGoBack(2);
  };

  const callAndCheckStatus = () => {
    // Don't start a new tick while the finalize from a prior tick is running.
    if (isFinalizingRef.current) return;
    totalTime.current = totalTime.current + 10;
    if (totalTime.current >= 120) {
      cancelIntervalAndSetStatus();
    } else {
      navigoServices
        .checkNavigoCreateStatus(orderId)
        .then((result) => {
          const res = result.data;
          console.log("res is", res, res.status);
          if (res && res.status === 2) {
            // Transaction successful. Stop polling payment status.
            if (intervalRef.current) clearInterval(intervalRef.current);
            const files = pendingPaidDocsRef.current?.files || [];
            if (files.length === 0) {
              // No documents to attach → finish (original behaviour).
              handleGoBack(2);
              return;
            }
            isFinalizingRef.current = true;
            finalizePaidDocs();
          } else if (res && res.status === 1) {
            cancelIntervalAndSetStatus();
          }
        })
        .catch((err) => {
          cancelIntervalAndSetStatus();
        });
    }
  };
  return (
    <div className="navigo-book-after-payment-container">
      <img src={checkIcon} />
      <h1>Payment Completed</h1>
      {status === 0 && (
        <>
          <CircularProgress className="navigo-book-after-payment-loader"></CircularProgress>
          <p className="navigo-book-after-payment-status">
            Creating appointment...
          </p>
        </>
      )}

      {status === 1 && (
        <p className="navigo-book-after-payment-status">
          ⚠️We couldn't confirm your booking, please try again after some time.
          If any sum is deducted at your end it will be refunded within 7
          working days.
        </p>
      )}
      {status === 1 && (
        <button
          className="navigo-book-after-payment-go-back"
          onClick={() => {
            window.location.href = isPB ? "/pb-home" : "/tab/home";
          }}
        >
          Go back
        </button>
      )}
    </div>
  );
}

export default BookAfterPayment;
